import Avatar from "@/components/Avatar";
import EmptyState from "@/components/EmptyState";
import ErrorBoundary from "@/components/ErrorBoundary";
import Icon from "@/components/Icon";
import Menu from "@/components/Menu";
import ObjectHeading from "@/components/ObjectHeading";
import { CardSkeleton, ObjectHeaderSkeleton, SectionHeadingSkeleton } from "@/components/Skeleton";
import Table, { TableHeading } from "@/components/Table";
import Tag from "@/components/Tag";
import usePageTitle from "@/hooks/usePageTitle";
import useAmplitude from "@/hooks/useAmplitude";
import { toDateTimeFormat } from "@/lib/dateHelpers";
import Banner from "@/components/Banner";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { DeleteInteractionModal, EditMessageModal, EditMethodModal, ShareInteractionModal } from "./modals";
import AiCard from "@/components/AiCard";
import { useAiQuery } from "@/lib/aiHelpers";
import { SummaryPayload, Interaction, InteractionRecipient, InteractionInput, InteractionMode } from "./types";
import AvatarDetails from "@/components/AvatarDetails";
import { CellContext } from "@tanstack/react-table";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import DetailsCard from "@/components/DetailsCard";
import SectionHeading from "@/components/SectionHeading";

import {
  DELETE_INTERACTION_MUTATION,
  UPDATE_INTERACTION_MUTATION,
  INTERACTION_CATEGORIES_METADATA_QUERY,
  InteractionCategoriesMetadataData,
  SHARE_INTERACTION_MUTATION,
  GET_PERSON_INTERACTIONS_QUERY
} from "./queries";
import Button from "@/components/Button";
import IconButton from "@/components/IconButton";
import { MaterialNames } from "@/components/material_symbol_names";
import InteractionDescriptionContent from "./InteractionDescriptionContent";
import { formatInteractionDisplayDate, getIcon, groupMessages, getInteractionTrackingData } from "./_utils";
import { formatAiSummary } from "@/lib/aiHelpers";
import { refetchPersonInteractionViews } from "@/components/interactions/refetchPersonInteractionViews";
import { getLinkDescription } from "@/components/interactions/_utils";
import { JobPost } from "@/pages/_layouts/jobs/types";
import { useCanManageInteractionsBool } from "@/hooks/useCapabilities";
import { JOB_POST_WITH_INTERACTIONS_QUERY } from "@/pages/_layouts/jobs/queries";
import NotesTable from "@/features/global/components/NotesTable";
import { useNoteDeepLink } from "@/features/global/components/NotesTable/useNoteDeepLink";
import TasksTable from "@/features/global/components/TasksTable";
import { NoteNotableValues } from "@/types";
import { PERSON_NOTES_QUERY, type PersonNotesQueryResponse } from "@/pages/people/[pgaId]/person/notes/queries";
import { useCanManageTasksBool } from "@/hooks/useCapabilities";
import { useFlags } from "@/hooks/useFeatureFlags";
import { isFlexInteractionEditLocked } from "@/layouts/AppLayout/IncomingCallEventIntegration/flexCallSummaryUtils";
import { ACTIVE_CALL_TRANSCRIPT_POLL_INTERVAL_MS } from "@/layouts/AppLayout/IncomingCallEventIntegration/useActiveCallTranscriptPoll";
import { useQueryPollingWhile } from "@/hooks/useQueryPollingWhile";

interface Person {
  id: string;
  displayName: string;
  firstName: string;
  lastName: string;
  pgaId: string;
  profilePhoto: string;
  currentStatus: string;
  employments: {
    isPrimary: boolean;
    facility: {
      name: string;
    };
  }[];
  classification: string;
  primarySectionAffiliation?: {
    section: {
      name: string;
    };
  };
}

function getParticipantFacilityName(participant: any) {
  return participant.employments?.find((e: any) => e.isPrimary)?.facility?.name || "";
}

interface InteractionDetailsProps {
  interactionId: string;
  pageContext: string;
  pgaId: string;
  sectionId?: string;
  jobPostId?: string;
  jobPost?: JobPost;
  onDelete?: () => void;
  onSuccess?: () => void;
  setActions?: (actions: () => JSX.Element) => void;
}

function safeGetIcon(mode: InteractionMode | null): MaterialNames {
  return mode ? getIcon(mode) : "account_circle";
}

function createActionsComponent(
  hasAccess: boolean,
  permissions: boolean,
  canDelete: boolean,
  editLocked: boolean,
  variables: InteractionInput,
  setVariables: React.Dispatch<React.SetStateAction<InteractionInput>>,
  setDeleteModalOpen: React.Dispatch<React.SetStateAction<boolean>>,
  setEditModalOpen: React.Dispatch<React.SetStateAction<boolean>>,
  setShareModalOpen: React.Dispatch<React.SetStateAction<boolean>>,
  mode: InteractionMode | null,
  categories: string[]
) {
  const ActionButtons = () => {
    if (!hasAccess) {
      return <></>;
    }

    const EDIT_BUTTON = {
      label: "Edit Interaction",
      onClick: () => {
        setVariables({
          ...variables,
          mode,
          activityType: categories?.[0],
          categories
        });
        setEditModalOpen(true);
      }
    };

    const DELETE_BUTTON = {
      label: "Delete Interaction",
      onClick: () => {
        setDeleteModalOpen(true);
      }
    };

    const SHARE_BUTTON = {
      label: "Share Interaction",
      onClick: () => {
        setShareModalOpen(true);
      }
    };

    const menuItems = [];

    if (permissions == true) {
      if (!editLocked) {
        menuItems.push(EDIT_BUTTON);
      }
      if (canDelete) {
        menuItems.push(DELETE_BUTTON);
      }
    }

    menuItems.push(SHARE_BUTTON);

    return (
      <>
        <div className="md:hidden">
          <Menu
            yOffset={8}
            {...{
              AnchorComponent: <IconButton name="more_vert" iconClassName="font-extrabold" />,
              menuItems: menuItems
            }}
          />
        </div>

        <div className="hidden md:block">
          <div className="space-x-3">
            {permissions && (
              <>
                {canDelete && (
                  <Button
                    variant="text"
                    onClick={() => {
                      setDeleteModalOpen(true);
                    }}
                  >
                    <div className="flex space-x-2">
                      <Icon name="delete" className="my-auto" size={20} />
                      <div className="my-auto">Delete Interaction</div>
                    </div>
                  </Button>
                )}
                {!editLocked && (
                  <Button
                    trailingIcon={false}
                    variant="outlined"
                    onClick={() => {
                      setVariables({
                        ...variables,
                        mode,
                        activityType: categories?.[0],
                        categories
                      });
                      setEditModalOpen(true);
                    }}
                  >
                    Edit Interaction
                  </Button>
                )}
              </>
            )}
            <Button
              trailingIcon={false}
              onClick={() => {
                setShareModalOpen(true);
              }}
            >
              <div className="flex space-x-2">
                <Icon name="ios_share" className="my-auto" size={20} />
                <div className="my-auto">Share Interaction</div>
              </div>
            </Button>
          </div>
        </div>
      </>
    );
  };

  ActionButtons.displayName = "ActionButtons";

  return ActionButtons;
}

// Utility function to check if a user has access to an interaction
const checkInteractionAccess = (interaction: Interaction): boolean => {
  return (
    !interaction.lastMessage.isPrivate || (interaction.lastMessage.isPrivate && interaction.lastMessage.haveAccess)
  );
};

// Utility function to check if a user can delete an interaction
const canDeleteInteraction = (permissions: String[] | string[] | null | undefined): boolean => {
  return permissions ? permissions.includes("delete") : false;
};

export default function InteractionDetails({
  pageContext,
  interactionId,
  pgaId,
  sectionId,
  jobPostId,
  jobPost,
  onDelete,
  onSuccess,
  setActions
}: InteractionDetailsProps) {
  const { trackEvent } = useAmplitude();
  const navigate = useNavigate();
  const location = useLocation();
  const apolloClient = useApolloClient();
  const { isCompactLayout } = useIsCompactLayout();
  usePageTitle("Interaction Details");

  const previousSetActionsRef = useRef(setActions);

  const { loading: generatingSummary, fetchData: generateSummary } = useAiQuery<SummaryPayload>(
    "career-consultation-summarizer"
  );

  // Hook for auto-generating a summary on initial load (if missing)
  const { loading: autoGeneratingSummary, fetchData: generateInsightsSummary } = useAiQuery<SummaryPayload>(
    "career-consultation-summarizer"
  );

  const autoSummaryAttemptedRef = useRef(false);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareVariables, setShareVariables] = useState<{ recipients: InteractionRecipient[]; notes: string }>({
    recipients: [],
    notes: ""
  });
  const [editMessageModalOpen, setEditMessageModalOpen] = useState(false);
  const [variables, setVariables] = useState<InteractionInput>({
    mode: null,
    description: null,
    interactionDate: null,
    activityType: null,
    categories: null,
    jobPostId: jobPostId || null,
    privacy_restricted: false
  });

  const [editInteractionMutation, { loading: updatingInteraction }] = useMutation(UPDATE_INTERACTION_MUTATION);
  const [deleteInteractionMutation, { loading: deletingInteraction }] = useMutation(DELETE_INTERACTION_MUTATION);
  const [shareInteractionMutation, { loading: sharingInteraction }] = useMutation(SHARE_INTERACTION_MUTATION);

  const asyncLoading = updatingInteraction || generatingSummary;

  const passedInteraction = location.state?.interaction as Interaction | undefined;

  const { data: allCategoriesData } = useQuery<InteractionCategoriesMetadataData>(
    INTERACTION_CATEGORIES_METADATA_QUERY,
    {
      fetchPolicy: "cache-first"
    }
  );

  const interactionCategoriesMetadata = useMemo(
    () => allCategoriesData?.interactionCategories ?? [],
    [allCategoriesData?.interactionCategories]
  );

  // Conditionally choose which query to use. This is being decided by a prop at the parent component level rather than the path
  // location in case there is any overlap in naming / variables or weirdness from navigation.
  const INTERACTION_QUERY = pageContext == "Person" ? GET_PERSON_INTERACTIONS_QUERY : JOB_POST_WITH_INTERACTIONS_QUERY;
  const QUERY_ID = pageContext == "Person" ? pgaId : jobPostId;
  const QUERY_VARIABLES = pageContext == "Person" ? { search: pgaId } : { jobPostId: QUERY_ID };

  const {
    data: interactionsData,
    loading,
    error,
    startPolling,
    stopPolling
  } = useQuery(INTERACTION_QUERY, {
    variables: { ...QUERY_VARIABLES, first: 100 },
    skip: !QUERY_ID,
    fetchPolicy: "network-only"
  });

  // Memo-ize the front-loaded interactions.
  const interactionIds = useMemo(() => {
    if (!interactionsData) return [];

    // We will never have both of these at the same time.
    const foundInteractions = interactionsData?.person?.interactions?.nodes || interactionsData?.jobPost?.interactions;

    return [...foundInteractions]
      .sort((a, b) => new Date(b.interactionDate).getTime() - new Date(a.interactionDate).getTime())
      .map((i) => i.id);
  }, [interactionsData]);

  // Establish navigational indexes.
  const currentIdx = interactionIds.indexOf(interactionId);
  const prevId = currentIdx > 0 ? interactionIds[currentIdx - 1] : null;
  const nextId = currentIdx >= 0 && currentIdx < interactionIds.length - 1 ? interactionIds[currentIdx + 1] : null;

  // Get interaction from passed data or find it in the interactions data
  // Always prefer fresh query data; only fall back to passedInteraction if query completed with no result
  const interaction = useMemo(() => {
    // If query returned data, use it
    if (interactionsData) {
      const queryInteraction =
        interactionsData?.person?.interactions?.nodes?.find((node: Interaction) => node && node.id === interactionId) ||
        interactionsData.jobPost?.interactions?.find((node: Interaction) => node && node.id === interactionId);
      if (queryInteraction) {
        return queryInteraction;
      }
      // Query completed but interaction not found - fall back to passedInteraction
      return passedInteraction || null;
    }

    // Query still loading or not started - return null to show loading state
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passedInteraction, interactionsData, currentIdx, interactionId]);

  const hasAccess = interaction ? checkInteractionAccess(interaction) : false;
  const mode = interaction?.mode || null;
  const categories = useMemo(() => interaction?.categories || [], [interaction?.categories]);
  const interactionJobPost = interaction?.jobPost;
  const interactionSummary = interaction?.summary;
  const hasValidAiSummary = Boolean(hasAccess && interaction?.showAiSummary);

  // Use jobPost from props if available, otherwise use the one from interaction
  const jobPostToUse = jobPost || interactionJobPost;

  // Get the supporting text based on the job post type
  const getJobPostSupportingText = (jobPost: any) => {
    if (jobPost?.category?.name) {
      return jobPost.category.name;
    }
    if (jobPost?.facility?.name) {
      return jobPost.facility.name;
    }
    return "--";
  };

  const participants = useMemo(() => (interaction?.participants as Person[]) || [], [interaction]);
  const currentParticipant = useMemo(
    () => participants.find((participant) => participant.pgaId === pgaId),
    [participants, pgaId]
  );
  const currentParticipantName = useMemo(
    () =>
      currentParticipant
        ? `${currentParticipant.firstName} ${currentParticipant.lastName}`
        : interaction?.relatedTo || "",
    [currentParticipant, interaction?.relatedTo]
  );

  const filteredParticipants = useMemo(
    () => participants.filter((p) => `${p.firstName} ${p.lastName}` !== currentParticipantName),
    [participants, currentParticipantName]
  );

  const managePeopleInteractions = useCanManageInteractionsBool();
  const canDelete = canDeleteInteraction(interaction?._permissions);

  const canManageTasks = useCanManageTasksBool();
  const showTasks = canManageTasks;
  const flags = useFlags();
  const transcriptSummaryEnabled = !!flags["flex-call-transcript-summary"];

  const flexInteractionEditLocked = useMemo(
    () =>
      transcriptSummaryEnabled &&
      isFlexInteractionEditLocked({
        messageContent: interaction?.lastMessage?.content,
        twilioFlexCall: interaction?.twilioFlexCall
      }),
    [interaction?.lastMessage?.content, interaction?.twilioFlexCall, transcriptSummaryEnabled]
  );

  useQueryPollingWhile(flexInteractionEditLocked, startPolling, stopPolling, ACTIVE_CALL_TRANSCRIPT_POLL_INTERVAL_MS);

  const { data: notesData } = useQuery<PersonNotesQueryResponse>(PERSON_NOTES_QUERY, {
    variables: {
      pgaId,
      first: 1,
      input: { notableType: [NoteNotableValues.INTERACTION], notableId: interactionId }
    },
    skip: !pgaId || !interactionId
  });
  const notesCount = notesData?.personNotes?.totalCount ?? 0;

  const { highlightNoteId, openNoteId, getCopyLink, setOpenNoteInUrl } = useNoteDeepLink({ dataReady: true });

  useEffect(() => {
    previousSetActionsRef.current = setActions;
  }, [setActions]);

  useEffect(() => {
    if (interaction) {
      setVariables((prev) => ({
        ...prev,
        privacy_restricted: interaction.lastMessage.isPrivate,
        jobPostId: interaction.jobPost?.id
      }));
    }
  }, [interaction]);

  // Fix for the actions component - runs only when necessary values change
  useEffect(() => {
    // Only proceed if there's a setActions function and data is loaded
    if (setActions && interaction) {
      const actionsComponentFn = createActionsComponent(
        hasAccess,
        managePeopleInteractions,
        canDelete,
        flexInteractionEditLocked,
        variables,
        setVariables,
        setDeleteModalOpen,
        setEditModalOpen,
        setShareModalOpen,
        mode,
        categories
      );

      setActions(actionsComponentFn);
    }
  }, [
    interaction,
    hasAccess,
    managePeopleInteractions,
    canDelete,
    flexInteractionEditLocked,
    mode,
    categories,
    setActions,
    variables,
    setDeleteModalOpen,
    setEditModalOpen,
    setShareModalOpen
  ]);

  useEffect(() => {
    autoSummaryAttemptedRef.current = false;
  }, [interactionId]);

  /*
   * Auto-generate a summary when the interaction first loads
   * and no summary is present. We utilise the `career-consultation-summarizer`
   * agent to create the summary, then PATCH the interaction with only the
   * `summary` field via `UPDATE_INTERACTION_MUTATION`.
   */
  useEffect(() => {
    async function generateAndSaveSummary() {
      if (!interaction) return;

      try {
        const summaryResponse = await generateInsightsSummary({
          mode: interaction.mode ?? null,
          subject: interaction.lastMessage?.content ?? null,
          comments: interaction.lastMessage?.content ?? null
        });

        const formattedSummary = formatAiSummary(summaryResponse);

        if (formattedSummary) {
          await editInteractionMutation({
            variables: {
              input: {
                interactionId: interactionId,
                summary: formattedSummary
              }
            },
            refetchQueries: [{ query: INTERACTION_QUERY, variables: { ...QUERY_VARIABLES, first: 100 } }]
          });
        }
      } catch (e) {
        console.error("Failed to auto-generate interaction summary", e);
      }
    }

    const shouldGenerate =
      interaction && hasAccess && !interaction.summary && !autoGeneratingSummary && !autoSummaryAttemptedRef.current;

    if (shouldGenerate) {
      autoSummaryAttemptedRef.current = true;
      generateAndSaveSummary();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    interaction,
    hasAccess,
    autoGeneratingSummary,
    generateInsightsSummary,
    editInteractionMutation,
    interactionId,
    pgaId
  ]);

  const NavigationArrows = () => {
    const currentPath = location.pathname;
    const basePath = currentPath.split("/").slice(0, -1).join("/");

    return (
      <div className="flex items-center gap-2">
        <IconButton
          name="navigate_before"
          ariaLabel="Previous Interaction"
          disabled={!prevId}
          variant="square"
          onClick={() => {
            if (prevId) navigate(`${basePath}/${prevId}`);
          }}
          data-testid="previous-button"
        />
        <IconButton
          name="navigate_next"
          ariaLabel="Next Interaction"
          disabled={!nextId}
          variant="square"
          onClick={() => {
            if (nextId) navigate(`${basePath}/${nextId}`);
          }}
          data-testid="next-button"
        />
      </div>
    );
  };

  if (error) {
    return (
      <div data-testid="error">
        <ErrorBoundary variant="inline" title="Unable to Load Interaction" message={error?.message} />
      </div>
    );
  }

  if (!interaction || (!passedInteraction && loading)) {
    return (
      <div className="mt-8">
        <ObjectHeaderSkeleton />
        <SectionHeadingSkeleton />
        <CardSkeleton />
        <SectionHeadingSkeleton />
        <CardSkeleton />
      </div>
    );
  }

  const summary = interaction.summary;
  const owner = interaction.owner;
  const date = new Date(interaction.interactionDate);

  // Use the interaction date for grouping
  const groupedMessages = groupMessages(interaction.messages, date);
  const messagesCount = interaction.messagesCount;

  const openTasksCount = interaction?.openTasksCount ?? 0;

  let metaData = [
    { label: "Modified", text: toDateTimeFormat(interaction.lastModifiedDate as string) || "-" },
    { label: "Messages", text: `${messagesCount}` },
    { label: "Notes", text: `${notesCount}` },
    ...(showTasks ? [{ label: "Open Tasks", text: `${openTasksCount}` }] : [])
  ];

  function trackingData() {
    if (!interaction) return {};

    return getInteractionTrackingData(variables, interaction, participants || [], summary || "", interactionId, pgaId);
  }

  async function deleteInteraction() {
    try {
      const { data } = await deleteInteractionMutation({
        variables: { id: interactionId }
      });

      if (data?.deleteInteraction?.success) {
        if (pageContext === "Person" && pgaId) {
          await refetchPersonInteractionViews(apolloClient);
        }

        trackEvent("Interaction Deleted", trackingData());
        setDeleteModalOpen(false);
        showSnackbar("Interaction deleted");
        if (onDelete) {
          onDelete();
        } else {
          if (jobPostId) {
            navigate(`/jobs/job-post/${jobPostId}/interactions`);
          } else if (sectionId) {
            navigate(`/sections/${sectionId}/jobs/job-post/${jobPostId}/interactions`);
          } else {
            navigate(`/people/${pgaId}/interactions/interactions`);
          }
        }
      } else {
        setDeleteModalOpen(false);
        showErrorSnackbar("There was a problem deleting this interaction. Please try again later");
      }
    } catch (error) {
      setDeleteModalOpen(false);
      showErrorSnackbar("There was a problem deleting this interaction. Please try again later");
    }
  }

  async function closeModals() {
    setEditModalOpen(false);
    setEditMessageModalOpen(false);
  }

  async function saveInteraction() {
    try {
      // Generate AI summary and capture the returned value
      const summaryResponse = await generateSummary({
        mode: variables.mode ?? null,
        subject: variables.description ?? null,
        comments: interaction?.lastMessage?.content ?? null
      });

      const formattedSummary = formatAiSummary(summaryResponse);

      const updateInput = {
        interactionId: interactionId,
        mode: variables.mode ?? null,
        description: variables.description,
        interactionDate: variables.interactionDate,
        categories: variables.categories ?? interaction?.categories ?? [],
        summary: formattedSummary,
        privacyRestricted: variables.privacy_restricted,
        jobPostId: variables.jobPostId || null
      };

      const { data } = await editInteractionMutation({
        variables: { input: updateInput },
        refetchQueries: [{ query: INTERACTION_QUERY, variables: { ...QUERY_VARIABLES, first: 10 } }],
        awaitRefetchQueries: true
      });

      if (data?.updateInteraction?.success) {
        if (pageContext === "Person" && pgaId) {
          await refetchPersonInteractionViews(apolloClient);
        }

        const trackingData = getInteractionTrackingData(
          variables,
          interaction!,
          participants,
          formattedSummary,
          interactionId,
          pgaId
        );

        trackEvent("update interaction", trackingData);
        closeModals();
        showSnackbar("Interaction saved");

        if (onSuccess) {
          onSuccess();
        }
      } else {
        throw new Error(data?.updateInteraction?.message || "Save failed");
      }
    } catch (error) {
      closeModals();
      showErrorSnackbar("There was a problem saving this interaction. Please try again later");
    }
  }

  async function shareInteraction() {
    try {
      const { data } = await shareInteractionMutation({
        variables: {
          input: {
            interaction: {
              id: interaction!.id,
              description: interaction!.lastMessage.content,
              owner: interaction!.owner.name,
              date: interaction!.interactionDate,
              mode: interaction!.mode,
              topic: interaction!.categories[0],
              participants: [
                interaction!.relatedTo,
                ...interaction!.participants.map(
                  (participant: any) => `${participant.firstName} ${participant.lastName}`
                )
              ]
            },
            recipients: shareVariables.recipients.map((recipient) => ({
              name: recipient.name,
              email: recipient.email
            })),
            notes: shareVariables.notes,
            pgaId: pgaId
          }
        }
      });

      if (data?.shareInteraction?.success) {
        trackEvent("Interaction Shared", trackingData());
        showSnackbar("Interaction shared");
        setShareModalOpen(false);
        setShareVariables({ recipients: [], notes: "" });
      } else {
        showErrorSnackbar("There was a problem sharing this interaction. Please try again later");
      }
    } catch {
      showErrorSnackbar("There was a problem sharing this interaction. Please try again later");
    }
  }

  return (
    <div data-testid="interaction-details-page">
      <div className="flex justify-between items-start gap-2">
        <ObjectHeading
          title={`${owner.name} ${getLinkDescription(mode as InteractionMode)} with ${currentParticipantName}`}
          iconName={safeGetIcon(mode)}
          metaData={metaData}
          isDetailsView
          tag={
            !hasAccess && interaction.lastMessage.isPrivate ? (
              <div className="mt-1">
                <Tag tagType="error" iconName={"lock"} title="Other departments cannot access this interaction">
                  <Icon name="lock" size={16} className="mr-1" />
                  Private to Department
                </Tag>
              </div>
            ) : undefined
          }
        />
        <div className="pt-8">{interactionIds.length > 1 && <NavigationArrows />}</div>
      </div>

      {!hasAccess && (
        <Banner
          className="mt-6"
          variant="warn"
          description="Access to this interaction is restricted"
          testId="interaction-restricted-banner"
        />
      )}

      {hasAccess && hasValidAiSummary && (
        <AiCard className="mt-6" title="Interaction Summary" text={interactionSummary as string} />
      )}
      {hasAccess && categories?.length > 0 && (
        <ul className="mt-6 flex flex-wrap gap-2" data-testid="interaction-tags">
          {categories.map((category: string) => (
            <li key={category}>
              <Tag tagType="secondary" size="large" iconClass="text-sys-brand-primary">
                {category}
              </Tag>
            </li>
          ))}
          {jobPostToUse && (
            <Tag tagType="tertiary" size="large" iconClass="text-sys-brand-primary">
              <Icon name="business_center" size={16} className="mr-1" />
              {jobPostToUse.name}
            </Tag>
          )}
        </ul>
      )}
      {hasAccess && (
        <>
          <SectionHeading
            heading="Description"
            body={
              managePeopleInteractions && !flexInteractionEditLocked
                ? "View or edit the description associated with this interaction."
                : "View the description associated with this interaction."
            }
          />
          <div className="border border-sys-brand-outline-variant rounded-lg p-4">
            <ul className="flex flex-col gap-4">
              {Object.entries(groupedMessages || {}).map(([monthYear, messages], i) => (
                <li key={i} data-testid={`messages-month-group-${i}`} className="flex flex-col items-center">
                  <div className="text-title-medium">{monthYear}</div>
                  <ul
                    className={`flex flex-col gap-12 mt-6 w-full pt-1 ${
                      hasAccess && managePeopleInteractions && !flexInteractionEditLocked
                        ? "group cursor-pointer hover:bg-sys-brand-surface-container"
                        : ""
                    }`}
                  >
                    {messages.map((message, i) => (
                      <li key={i} className={`flex max-w-full gap-3 ${message.fromOwner ? "self-end" : "self-start"}`}>
                        {!message.fromOwner && <Avatar picture={message.fromProfilePhoto} iconClassName="p-6" />}
                        <div className="md:max-w-[93%] max-w-[80%] relative">
                          {managePeopleInteractions && !flexInteractionEditLocked && (
                            <div className="border w-fit px-3 rounded-lg shadow-lg border-sys-brand-outline opacity-0 transition-all duration-200 absolute -top-12 right-0 bg-sys-brand-surface group-hover:opacity-100">
                              <IconButton
                                name="edit"
                                className="!m-0"
                                onClick={() => {
                                  setVariables({
                                    ...variables,
                                    interactionDate: interaction.interactionDate.toString().split("T")[0],
                                    description: interaction.lastMessage.content
                                  });
                                  setEditMessageModalOpen(true);
                                }}
                              />
                            </div>
                          )}
                          <div
                            className={`rounded-lg py-3 px-4 flex gap-3 ${
                              message.fromOwner
                                ? "bg-sys-brand-secondary-container"
                                : "bg-sys-brand-surface-container-high"
                            }`}
                          >
                            {message.fromOwner && message.isPrivate && <Icon name="lock" size={18} className="mt-1" />}
                            {message.isPrivate && !message.haveAccess ? (
                              <p className="text-body-large md:max-w-[800px] md:w-full w-[85%] whitespace-pre-wrap break-words">
                                You don't have access to this message
                              </p>
                            ) : (
                              <InteractionDescriptionContent
                                content={message.content}
                                className="md:max-w-[800px] md:w-full w-[85%]"
                              />
                            )}
                            {!message.fromOwner && message.isPrivate && (
                              <Icon name="lock" size={18} className="ml-auto mt-1" />
                            )}
                          </div>
                          <p className={`mt-2 text-label-small ${message.fromOwner && "text-right  "}`}>
                            {formatInteractionDisplayDate(interaction.interactionDate) || ""}
                          </p>
                        </div>
                        {message.fromOwner && <Avatar picture={message.fromProfilePhoto} iconClassName="p-6" />}
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          </div>

          {jobPostToUse && (
            <>
              <SectionHeading heading="Associated Items" body="View associated items with this interaction" />

              <div className="w-full md:w-1/3">
                <DetailsCard
                  options={[
                    {
                      overline: "Job",
                      iconName: "business_center",
                      headline: jobPostToUse.name || "--",
                      supportingText: getJobPostSupportingText(jobPostToUse),
                      action: () =>
                        navigate(
                          sectionId
                            ? `/sections/${sectionId}/jobs/job-post/${jobPostToUse.id}`
                            : `/jobs/job-post/${jobPostToUse.id}`
                        ),
                      actionIcon: "navigate_next"
                    }
                  ]}
                />
              </div>
            </>
          )}

          {filteredParticipants.length > 0 && (
            <>
              <div data-testid="additional-people-included">
                <TableHeading
                  heading="Additional People Included"
                  body="Select a person to view their corresponding interaction"
                />
              </div>
              <Table
                data={filteredParticipants}
                loading={loading}
                data-testid="additional-people-table"
                onClickRow={(row) => navigate(`/people/${row.original.pgaId}`)}
                columns={[
                  ...(isCompactLayout
                    ? [
                        {
                          cell: (cellProps: CellContext<Person, unknown>) => {
                            const { profilePhoto } = cellProps.row.original;
                            return <Avatar picture={profilePhoto} />;
                          },
                          id: "avatar"
                        },
                        {
                          cell: (cellProps: CellContext<Person, unknown>) => {
                            const { firstName, lastName } = cellProps.row.original;
                            return (
                              <div className="text-body-medium">
                                {firstName} {lastName}
                              </div>
                            );
                          },
                          id: "fullName"
                        }
                      ]
                    : [
                        {
                          size: 230,
                          header: "Name",
                          id: "fullName",
                          cell: ({ row: { original: person } }: { row: { original: Person } }) => {
                            const { firstName, lastName, profilePhoto } = person;
                            return <AvatarDetails picture={profilePhoto} title={`${firstName} ${lastName}`} text="" />;
                          }
                        }
                      ]),
                  { accessorKey: "pgaId", header: "PGA ID", id: "pgaId", enableSorting: false },
                  { accessorKey: "classification", header: "Class", id: "class", enableSorting: false },
                  { accessorKey: "currentStatus", header: "Status", id: "status", enableSorting: false },
                  {
                    accessorKey: "primary_section_affiliation",
                    header: "Section",
                    size: 200,
                    enableSorting: false,
                    cell: ({ row: { original: person } }) =>
                      person.primarySectionAffiliation?.section?.name || <div className="opacity-20">--</div>
                  },
                  {
                    accessorKey: "primaryFacility",
                    header: "Primary Facility",
                    enableSorting: false,
                    size: 300,
                    cell: (cellProps) =>
                      getParticipantFacilityName(cellProps.row.original) || <div className="opacity-20">--</div>
                  }
                ]}
                renderFilteredEmptyState={() => <EmptyState title="No Results Found" iconName="today" />}
              />
            </>
          )}
        </>
      )}
      {hasAccess && (
        <div>
          <NotesTable
            variant="top-level"
            notesQuery={PERSON_NOTES_QUERY}
            queryVariables={{ pgaId }}
            notableTypeFilter={[NoteNotableValues.INTERACTION]}
            notableIdFilter={interactionId}
            hiddenFilters={["sourceType", "visibility"]}
            hideControls
            tableHeading="Notes"
            tableBody="View and manage notes for this interaction"
            noteEditing={{
              enabled: managePeopleInteractions,
              notableId: interactionId,
              notableType: NoteNotableValues.INTERACTION,
              refetchQueries: [PERSON_NOTES_QUERY],
              pgaId,
              objectType: "person"
            }}
            highlightNoteId={highlightNoteId}
            openNoteId={openNoteId}
            onOpenNoteChange={setOpenNoteInUrl}
            getCopyLink={getCopyLink}
          />
        </div>
      )}
      {hasAccess && showTasks && (
        <div>
          <TasksTable
            referenceableId={interactionId}
            referenceableType="INTERACTION"
            tableHeading="Tasks"
            tableBody="View and manage tasks for this interaction"
            hideControls
          />
        </div>
      )}

      <EditMethodModal
        open={editModalOpen}
        setOpen={setEditModalOpen}
        variables={variables}
        setVariables={setVariables}
        saveInteraction={saveInteraction}
        interactionPrivate={interaction.lastMessage.isPrivate}
        disabled={asyncLoading}
        interactionCategories={interactionCategoriesMetadata}
        jobPostId={jobPostId}
      />
      <DeleteInteractionModal
        open={deleteModalOpen}
        setOpen={setDeleteModalOpen}
        participantsCount={participants.length}
        deleteInteraction={deleteInteraction}
        disabled={deletingInteraction}
      />
      <EditMessageModal
        open={editMessageModalOpen}
        setOpen={setEditMessageModalOpen}
        variables={variables}
        setVariables={setVariables}
        saveInteraction={saveInteraction}
        disabled={asyncLoading}
      />
      <ShareInteractionModal
        open={shareModalOpen}
        setOpen={setShareModalOpen}
        shareInteraction={shareInteraction}
        isPrivate={interaction.lastMessage.isPrivate}
        shareVariables={shareVariables}
        setShareVariables={setShareVariables}
        disabled={sharingInteraction}
      />
    </div>
  );
}
